import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, Users, ShieldCheck, Sparkles, ArrowRight, CheckCircle2, Search,
  FileText, Handshake, ClipboardCheck, Package, Scale, ScrollText, Trophy,
  Wrench, HardHat, Store, Layers, Mail, Phone, Globe, Linkedin, MessageCircle,
  ChevronDown, UserPlus, Briefcase,
} from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import PublicSubNav from '../components/PublicSubNav';
import PublicSidebar from '../components/PublicSidebar';
import PublicLatestTables from '../components/PublicLatestTables';
import PublicRightSidebar from '../components/PublicRightSidebar';
import PublicFooter from '../components/PublicFooter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUPPORT_EMAIL = 'info@plusultrainnovation.com';
const WHATSAPP = '918369211947';

const LandingPage = () => {
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [tenderCount, setTenderCount] = useState(null);
  const [q, setQ] = useState({ state: '', category: '', keyword: '' });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/founding-offer`).then(r => setOffer(r.data)).catch(() => {});
    axios.get(`${API}/tenders/public-search?limit=1`).then(r => setTenderCount(r.data?.total ?? null)).catch(() => {});
  }, []);

  const campaignLive = offer && offer.offer_status === 'open';

  const doTenderSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (q.keyword) p.set('keyword', q.keyword);
    if (q.state) p.set('state', q.state);
    if (q.category) p.set('category', q.category);
    navigate(`/tenders/public-search?${p.toString()}`);
  };

  return (
    <div className="bg-white text-[#1E1B4B] antialiased">
      {/* ═════════ NEW HEADER + SIDEBAR (Feb 2026 IA restructure) ═════════ */}
      <PublicHeader onOpenSidebar={() => setSidebarOpen(true)} />
      <PublicSubNav />
      <div className="flex">
        <PublicSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="flex-1 min-w-0" data-testid="landing-main">

      {/* ═════════ 3-COLUMN MAIN LAYOUT (welcome + stats + tables ‖ right sidebar) ═════════ */}
      <div className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6">
          <div className="min-w-0 space-y-5">
            {/* Welcome banner + inline stats */}
            <WelcomeStats />
            {/* Latest Tenders + Corrigendum, stacked vertically per reference */}
            <PublicLatestTables bare stacked />
          </div>
          <PublicRightSidebar />
        </div>
      </div>

      {/* ═════════ QUICK BENEFITS ═════════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="quick-benefits">
        {[
          {icon: ShieldCheck, label: 'Transparent Procurement', body: 'Every quote, negotiation and award recorded in an immutable audit trail.'},
          {icon: Trophy,      label: 'Best Value',              body: 'Multi-round negotiations + verified suppliers deliver measurable savings.'},
          {icon: CheckCircle2,label: 'Verified Supplier Network', body: 'GST, PAN & category-verified suppliers matched by AI on your requirements.'},
          {icon: Sparkles,    label: 'Easy to Use',              body: 'Zero training needed. Managing Committees onboard in under 10 minutes.'},
        ].map((b, i) => (
          <div key={i} className="group p-6 rounded-2xl bg-slate-50 border border-slate-100 hover:border-[#5B21B6]/30 hover:shadow-lg transition-all hover:-translate-y-1" data-testid={`benefit-${i}`}>
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <b.icon className="w-5 h-5 text-[#F59E0B]" />
            </div>
            <div className="font-bold text-[#1E1B4B] mb-1">{b.label}</div>
            <div className="text-sm text-slate-600">{b.body}</div>
          </div>
        ))}
      </section>

      {/* ═════════ FOUNDING MEMBER CAMPAIGN (dynamic) OR PLATFORM STATS ═════════ */}
      {campaignLive ? (
        <FoundingCampaign offer={offer} />
      ) : (
        <LiveStats offer={offer} tenderCount={tenderCount} />
      )}

      {/* ═════════ FEATURES ═════════ */}
      <section id="features" className="bg-slate-50 py-20" data-testid="features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs font-bold text-[#5B21B6] uppercase tracking-wider mb-2">Enterprise-grade Features</div>
            <h2 className="text-4xl font-black">Everything your Society needs to procure with confidence.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {icon: FileText,       t: 'Tender Management',      d: 'Publish tenders, invite verified suppliers, collect quotes.'},
              {icon: Users,          t: 'Supplier Network',       d: 'Curated, GST-verified suppliers matched by category & location.'},
              {icon: Scale,          t: 'Comparative Statement',  d: 'One-click side-by-side quotation comparison with a downloadable PDF.'},
              {icon: Handshake,      t: 'Negotiation Workflow',   d: 'Multi-round negotiations with a locked, timestamped audit trail.'},
              {icon: ClipboardCheck, t: 'Committee Approval',     d: 'Structured MoM + approval-letter workflow with legal compliance.'},
              {icon: Package,        t: 'Purchase Orders',        d: 'Instant PO generation, delivery tracking & work-completion sign-off.'},
              {icon: ScrollText,     t: 'Audit Trail',            d: 'Every action logged. Regulator- and lawyer-ready records on demand.'},
            ].map((f,i)=>(
              <div key={i} className="p-6 rounded-2xl bg-white border border-slate-100 hover:border-[#5B21B6]/40 hover:shadow-xl transition-all" data-testid={`feature-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#1E1B4B] to-[#5B21B6] flex items-center justify-center mb-3">
                  <f.icon className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div className="font-bold mb-1 text-[#1E1B4B]">{f.t}</div>
                <div className="text-sm text-slate-600">{f.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═════════ WHO IS IT FOR ═════════ */}
      <section id="for-buyers" className="py-20" data-testid="who-for">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs font-bold text-[#5B21B6] uppercase tracking-wider mb-2">Who is it for</div>
            <h2 className="text-4xl font-black">Built for the entire housing-society value chain.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              {icon: Building2, t: 'Co-operative Housing Societies', c:'#5B21B6'},
              {icon: Layers,    t: 'Facility Management Companies',   c:'#7C3AED'},
              {icon: HardHat,   t: 'Contractors',                     c:'#1E1B4B'},
              {icon: Store,     t: 'Vendors',                         c:'#5B21B6'},
              {icon: Wrench,    t: 'Service Providers',               c:'#7C3AED'},
            ].map((w,i)=>(
              <div key={i} id={w.t === 'Facility Management Companies' ? 'for-suppliers' : undefined} className="p-6 rounded-2xl bg-white border-2 border-slate-100 hover:border-[#F59E0B] hover:shadow-2xl transition-all hover:-translate-y-1 text-center" data-testid={`who-${i}`}>
                <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{background:`linear-gradient(135deg, ${w.c}, #7C3AED)`}}>
                  <w.icon className="w-7 h-7 text-[#F59E0B]" />
                </div>
                <div className="font-bold text-sm text-[#1E1B4B]">{w.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═════════ TENDER SEARCH ═════════ */}
      <section id="tenders-status" className="bg-gradient-to-br from-[#1E1B4B] to-[#1e1b4b] py-20 text-white" data-testid="tender-search-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="text-xs font-bold text-[#F59E0B] uppercase tracking-wider mb-2">Public Tender Search</div>
            <h2 className="text-4xl font-black">Discover live tenders open to your business.</h2>
            <p className="text-slate-300 mt-2">Filter by state, district, category or keyword. {tenderCount != null && <><strong className="text-[#F59E0B]">{tenderCount}</strong> tenders indexed.</>}</p>
          </div>
          <form onSubmit={doTenderSearch} className="bg-white rounded-2xl p-6 grid md:grid-cols-4 gap-3 shadow-2xl">
            <input placeholder="Keyword (e.g. Painting)" value={q.keyword} onChange={e=>setQ({...q,keyword:e.target.value})} className="px-4 py-3 rounded-lg border border-slate-200 text-[#1E1B4B] text-sm focus:border-[#5B21B6] focus:outline-none" data-testid="ts-keyword"/>
            <select value={q.state} onChange={e=>setQ({...q,state:e.target.value})} className="px-4 py-3 rounded-lg border border-slate-200 text-[#1E1B4B] text-sm" data-testid="ts-state">
              <option value="">All States</option>
              {['Maharashtra','Karnataka','Delhi','Tamil Nadu','Gujarat','Telangana','West Bengal'].map(s=><option key={s}>{s}</option>)}
            </select>
            <select value={q.category} onChange={e=>setQ({...q,category:e.target.value})} className="px-4 py-3 rounded-lg border border-slate-200 text-[#1E1B4B] text-sm" data-testid="ts-category">
              <option value="">All Categories</option>
              {['Housekeeping','Security','Painting','Lift Maintenance','Plumbing','Electrical','Fire Safety','Landscaping'].map(c=><option key={c}>{c}</option>)}
            </select>
            <button type="submit" className="px-4 py-3 rounded-lg bg-gradient-to-r from-[#5B21B6] to-[#7C3AED] text-white font-bold flex items-center justify-center gap-2" data-testid="ts-submit-btn">
              <Search className="w-4 h-4"/> Search
            </button>
          </form>
        </div>
      </section>

      {/* ═════════ TRUST / WHY ═════════ */}
      <section id="trust" className="py-20" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs font-bold text-[#5B21B6] uppercase tracking-wider mb-2">Why ProcureOne Ultra?</div>
              <h2 className="text-4xl font-black mb-6">A procurement backbone built on trust, not spreadsheets.</h2>
              <p className="text-slate-600 mb-6">
                Traditional society procurement is opaque, verbal and unaudited. ProcureOne Ultra rebuilds it as a legally-defensible digital workflow — verified suppliers, competitive quotations, structured negotiations, and a rupee-by-rupee audit trail.
              </p>
              <div className="space-y-3">
                {[
                  'Transparent Procurement — every step captured',
                  'Verified Suppliers — GST, PAN & category-checked',
                  'Digital Audit Trail — regulator & auditor ready',
                  'Competitive Quotations — multi-round negotiations',
                  'Structured Procurement Workflow — 12-stage lifecycle',
                ].map((t,i)=>(
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#F59E0B] mt-0.5 flex-shrink-0"/>
                    <span className="text-[#1E1B4B] font-medium">{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="p-8 rounded-3xl bg-gradient-to-br from-[#1E1B4B] to-[#5B21B6] text-white shadow-2xl">
                <div className="text-xs font-bold text-[#F59E0B] uppercase tracking-wider mb-3">Real Impact · Founding Deployment</div>
                <div className="grid grid-cols-2 gap-6">
                  <div><div className="text-4xl font-black text-[#F59E0B]">100%</div><div className="text-xs text-slate-300 mt-1">Audit Trail Coverage</div></div>
                  <div><div className="text-4xl font-black text-[#F59E0B]">&lt; 10m</div><div className="text-xs text-slate-300 mt-1">Onboarding Time</div></div>
                  <div><div className="text-4xl font-black text-[#F59E0B]">24×7</div><div className="text-xs text-slate-300 mt-1">Committee Access</div></div>
                  <div><div className="text-4xl font-black text-[#F59E0B]">12</div><div className="text-xs text-slate-300 mt-1">Stage Procurement Lifecycle</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═════════ FOOTER ═════════ */}
      <PublicFooter />
        </main>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Welcome banner + inline 5-tile stats — replaces the old hero
// illustration. All values pulled live from /api/public/stats. If a
// counter is missing (transient DB error) we render "—" rather than
// any placeholder.
// ═══════════════════════════════════════════════════════════════════
const inrCompact = (v) => {
  const n = Number(v || 0);
  if (!isFinite(n) || n === 0) return '₹0';
  if (n >= 1_00_00_000) return '₹' + (n / 1_00_00_000).toFixed(1) + ' Cr';
  if (n >= 1_00_000)    return '₹' + (n / 1_00_000).toFixed(1) + ' L';
  if (n >= 1_000)       return '₹' + (n / 1_000).toFixed(1) + ' K';
  return '₹' + Math.round(n).toLocaleString('en-IN');
};

const nfInt = new Intl.NumberFormat('en-IN');

const WelcomeStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const fetchStats = () => axios.get(`${API}/public/stats`)
      .then(r => { if (mounted) { setStats(r.data); setLoading(false); } })
      .catch(() => { if (mounted) setLoading(false); });
    fetchStats();
    // Poll every 20s so newly-approved societies + suppliers reflect in near-real-time.
    const iv = setInterval(fetchStats, 20000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  const tiles = [
    { key: 'societies', icon: Building2,   label: 'Registered Societies',    value: stats?.registered_societies,   fmt: 'int' },
    { key: 'suppliers', icon: Store,       label: 'Active Suppliers',         value: stats?.active_suppliers,       fmt: 'int' },
    { key: 'tenders',   icon: FileText,    label: 'Tenders Published',        value: stats?.tenders_published,      fmt: 'int' },
    { key: 'value',     icon: Trophy,      label: 'Total Tender Value',       value: stats?.total_tender_value_inr, fmt: 'inr' },
    { key: 'savings',   icon: ShieldCheck, label: 'Total Savings Achieved',   value: stats?.total_savings_inr,      fmt: 'inr' },
  ];

  const display = (t) => {
    if (loading) return '…';
    if (t.value == null) return '—';
    if (t.fmt === 'inr') return inrCompact(t.value);
    return nfInt.format(t.value);
  };

  return (
    <div data-testid="welcome-stats">
      <div className="bg-white border border-slate-200 rounded-xl p-4 lg:p-5 mb-4 shadow-sm">
        <h1 className="text-xl lg:text-2xl font-black text-[#0A1F44] mb-1" data-testid="welcome-title">
          Welcome to ProcureOne Ultra
        </h1>
        <p className="text-sm text-slate-600 max-w-3xl">
          A common eProcurement platform designed exclusively for Co-op Housing Societies to manage
          procurement end-to-end with transparency, compliance and efficiency.
        </p>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-3 lg:p-4 shadow-sm grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-2">
        {tiles.map(t => {
          const Icon = t.icon;
          return (
            <div key={t.key} className="flex items-center gap-2.5" data-testid={`welcome-stat-${t.key}`}>
              <div className="w-10 h-10 shrink-0 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="min-w-0">
                <div
                  className="text-lg lg:text-xl font-black text-[#0A1F44] leading-tight tabular-nums"
                  data-testid={`welcome-stat-value-${t.key}`}
                >
                  {display(t)}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 leading-tight">{t.label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Dynamic Founding Member Campaign — pulls counts from /api/founding-offer
// ═══════════════════════════════════════════════════════════════════
const FoundingCampaign = ({ offer }) => {
  const navigate = useNavigate();
  const b = offer?.buyer   || { used_slots: 0, max_slots: 100, remaining: 100 };
  const s = offer?.supplier|| { used_slots: 0, max_slots: 100, remaining: 100 };
  const bPct = Math.round((b.used_slots / b.max_slots) * 100);
  const sPct = Math.round((s.used_slots / s.max_slots) * 100);
  return (
    <section id="founding-offer" className="py-20 bg-gradient-to-br from-[#faf7ff] via-white to-[#fffbeb]" data-testid="founding-campaign">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/40 text-[#1E1B4B] text-xs font-bold mb-3">
            <Sparkles className="w-3 h-3"/> FOUNDING MEMBER CAMPAIGN · LIMITED SLOTS
          </div>
          <h2 className="text-4xl font-black">Be one of the first <span className="text-[#5B21B6]">200 Founding Members</span>.</h2>
          <p className="text-slate-600 mt-2">Founding pricing. Founding badge. Founding voice in the product roadmap. Open until <strong className="text-[#1E1B4B]">the first 100 Buyers and first 100 Suppliers</strong> register.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {[
            {label:'Buyer Founding Members',  d:b, pct:bPct, role:'buyer',    color:'#5B21B6', icon: Building2},
            {label:'Supplier Founding Members',d:s, pct:sPct, role:'supplier', color:'#7C3AED', icon: Store},
          ].map((c,i)=>(
            <div key={i} className="p-8 rounded-3xl bg-white border-2 border-slate-100 hover:border-[#F59E0B] shadow-lg" data-testid={`founding-card-${c.role}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{background:`linear-gradient(135deg, ${c.color}, #F59E0B)`}}><c.icon className="w-5 h-5 text-white"/></div>
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Slots Filled</div>
                  <div className="text-3xl font-black text-[#1E1B4B]" data-testid={`founding-count-${c.role}`}>{c.d.used_slots}<span className="text-slate-400 text-xl">/{c.d.max_slots}</span></div>
                </div>
              </div>
              <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden mb-3">
                <div className="h-full rounded-full transition-all" style={{width:`${c.pct}%`, background:`linear-gradient(90deg, ${c.color}, #F59E0B)`}}/>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div className="text-slate-600"><strong className="text-[#1E1B4B]" data-testid={`founding-remaining-${c.role}`}>{c.d.remaining}</strong> remaining · {c.label}</div>
                <button onClick={()=>navigate(`/register?role=${c.role}&founding=1`)} disabled={c.d.is_full} className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#5B21B6] to-[#7C3AED] text-white text-xs font-bold disabled:opacity-40" data-testid={`founding-register-${c.role}`}>
                  {c.d.is_full ? 'SLOTS FULL' : 'Register Now'} <ArrowRight className="inline w-3 h-3 ml-1"/>
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-3 gap-3 p-5 rounded-2xl bg-slate-50 border border-slate-100 text-sm">
          {[
            {t:'Offer Valid Until', v:'First 100 Buyers + First 100 Suppliers Register'},
            {t:'For',              v:'First 100 Buyers + First 100 Suppliers'},
            {t:'Benefits',         v:'50% off Y1 · Y2 100% free · Founding Badge · Roadmap voice'},
          ].map((x,i)=>(
            <div key={i}><div className="text-[10px] font-bold text-[#5B21B6] uppercase tracking-wider">{x.t}</div><div className="text-[#1E1B4B] font-semibold">{x.v}</div></div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Live Platform Statistics (post-campaign fallback)
// ═══════════════════════════════════════════════════════════════════
const LiveStats = ({ offer, tenderCount }) => (
  <section className="py-20 bg-gradient-to-br from-[#faf7ff] to-white" data-testid="live-stats">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10">
        <div className="text-xs font-bold text-[#5B21B6] uppercase tracking-wider mb-2">Live Platform Statistics</div>
        <h2 className="text-4xl font-black">The procurement backbone of Indian housing societies.</h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {t:'Total Buyers',     v: offer?.buyer?.used_slots ?? '—'},
          {t:'Total Suppliers',  v: offer?.supplier?.used_slots ?? '—'},
          {t:'Active Tenders',   v: tenderCount ?? '—'},
          {t:'Verified Network', v: offer ? (offer.buyer.used_slots + offer.supplier.used_slots) : '—'},
        ].map((s,i)=>(
          <div key={i} className="p-6 rounded-2xl bg-white border border-slate-100 text-center hover:shadow-lg transition-shadow" data-testid={`stat-${i}`}>
            <div className="text-4xl font-black text-[#5B21B6]">{s.v}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">{s.t}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default LandingPage;
